"use client";

import { useCallback, useEffect } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";

import { TweetTone, TweetLength } from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UseTweetGeneratorParams,
  UseTweetGeneratorReturn,
} from "~/modules/main/hooks/use-tweet-generator/use-tweet-generator.interface";

// Global state atoms
const topicAtom = atom<string>("");
const toneAtom = atom<TweetTone | null>(null);
const audienceAtom = atom<string>("");
const keywordsAtom = atom<string>("");
const lengthAtom = atom<TweetLength>(TweetLength.MEDIUM);
const generatedTweetAtom = atom<string>("");
const charCountAtom = atom<number>(0);
const lastGeneratedAtAtom = atom<Date | null>(null);
const isExportDialogOpenAtom = atom<boolean>(false);
const exportFilenameAtom = atom<string>("my-tweet");

// Validation atoms
const topicErrorAtom = atom<string | null>(null);
const toneErrorAtom = atom<string | null>(null);
const audienceErrorAtom = atom<string | null>(null);

export function useTweetGenerator(
  params: UseTweetGeneratorParams,
): UseTweetGeneratorReturn {
  // State management
  const [topic, setTopicState] = useAtom(topicAtom);
  const [tone, setToneState] = useAtom(toneAtom);
  const [audience, setAudienceState] = useAtom(audienceAtom);
  const [keywords, setKeywordsState] = useAtom(keywordsAtom);
  const [length, setLengthState] = useAtom(lengthAtom);
  const [generatedTweet, setGeneratedTweet] = useAtom(generatedTweetAtom);
  const [charCount, setCharCount] = useAtom(charCountAtom);
  const [lastGeneratedAt, setLastGeneratedAt] = useAtom(lastGeneratedAtAtom);
  const [isExportDialogOpen, setIsExportDialogOpen] = useAtom(
    isExportDialogOpenAtom,
  );
  const [exportFilename, setExportFilenameState] = useAtom(exportFilenameAtom);

  // Validation state
  const [topicError, setTopicError] = useAtom(topicErrorAtom);
  const [toneError, setToneError] = useAtom(toneErrorAtom);
  const [audienceError, setAudienceError] = useAtom(audienceErrorAtom);

  // Initialize with params
  useEffect(() => {
    if (params.initialTopic !== undefined) {
      setTopicState(params.initialTopic);
    }
    if (params.initialTone !== undefined) {
      setToneState(params.initialTone);
    }
    if (params.initialAudience !== undefined) {
      setAudienceState(params.initialAudience);
    }
    if (params.initialKeywords !== undefined) {
      setKeywordsState(params.initialKeywords);
    }
    if (params.initialLength !== undefined) {
      setLengthState(params.initialLength);
    }
  }, [
    params.initialTopic,
    params.initialTone,
    params.initialAudience,
    params.initialKeywords,
    params.initialLength,
    setTopicState,
    setToneState,
    setAudienceState,
    setKeywordsState,
    setLengthState,
  ]);

  // TRPC mutations
  const generateTweetMutation =
    trpcReactQueryApi.main.tweetGenerator.generateTweet.useMutation({
      onSuccess: (data) => {
        setGeneratedTweet(data.content);
        setCharCount(data.charCount);
        setLastGeneratedAt(new Date());
        toast.success("Tweet generated successfully!");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to generate tweet");
      },
    });

  const regenerateTweetMutation =
    trpcReactQueryApi.main.tweetGenerator.regenerateTweet.useMutation({
      onSuccess: (data) => {
        setGeneratedTweet(data.content);
        setCharCount(data.charCount);
        setLastGeneratedAt(new Date());
        toast.success("Tweet regenerated successfully!");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to regenerate tweet");
      },
    });

  // Validation helper
  const validateInputs = useCallback((): boolean => {
    let isValid = true;

    if (!topic || topic.trim().length === 0) {
      setTopicError("Topic is required");
      isValid = false;
    } else if (topic.length > 120) {
      setTopicError("Topic must be 120 characters or less");
      isValid = false;
    } else {
      setTopicError(null);
    }

    if (!tone) {
      setToneError("Tone is required");
      isValid = false;
    } else {
      setToneError(null);
    }

    if (!audience || audience.trim().length === 0) {
      setAudienceError("Target audience is required");
      isValid = false;
    } else if (audience.length > 120) {
      setAudienceError("Audience must be 120 characters or less");
      isValid = false;
    } else {
      setAudienceError(null);
    }

    return isValid;
  }, [topic, tone, audience, setTopicError, setToneError, setAudienceError]);

  // Mutations
  const setTopic = useCallback(
    (newTopic: string) => {
      setTopicState(newTopic);
      if (topicError) setTopicError(null);
    },
    [setTopicState, topicError, setTopicError],
  );

  const setTone = useCallback(
    (newTone: TweetTone | null) => {
      setToneState(newTone);
      if (toneError) setToneError(null);
    },
    [setToneState, toneError, setToneError],
  );

  const setAudience = useCallback(
    (newAudience: string) => {
      setAudienceState(newAudience);
      if (audienceError) setAudienceError(null);
    },
    [setAudienceState, audienceError, setAudienceError],
  );

  const setKeywords = useCallback(
    (newKeywords: string) => {
      setKeywordsState(newKeywords);
    },
    [setKeywordsState],
  );

  const setLength = useCallback(
    (newLength: TweetLength) => {
      setLengthState(newLength);
    },
    [setLengthState],
  );

  const openExportDialog = useCallback(
    (initialFilename?: string) => {
      if (initialFilename) {
        setExportFilenameState(initialFilename);
      }
      setIsExportDialogOpen(true);
    },
    [setExportFilenameState, setIsExportDialogOpen],
  );

  const closeExportDialog = useCallback(() => {
    setIsExportDialogOpen(false);
  }, [setIsExportDialogOpen]);

  const setExportFilename = useCallback(
    (filename: string) => {
      setExportFilenameState(filename);
    },
    [setExportFilenameState],
  );

  const resetCompose = useCallback(() => {
    setTopicState("");
    setToneState(null);
    setAudienceState("");
    setKeywordsState("");
    setLengthState(TweetLength.MEDIUM);
    setTopicError(null);
    setToneError(null);
    setAudienceError(null);
  }, [
    setTopicState,
    setToneState,
    setAudienceState,
    setKeywordsState,
    setLengthState,
    setTopicError,
    setToneError,
    setAudienceError,
  ]);

  const clearValidation = useCallback(() => {
    setTopicError(null);
    setToneError(null);
    setAudienceError(null);
  }, [setTopicError, setToneError, setAudienceError]);

  // Commands
  const generateTweet = useCallback(async () => {
    if (!validateInputs()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!tone) {
      toast.error("Please select a tone");
      return;
    }

    await generateTweetMutation.mutateAsync({
      topic: topic.trim(),
      tone,
      audience: audience.trim(),
      keywords: keywords.trim() || null,
      length,
    });
  }, [
    validateInputs,
    tone,
    topic,
    audience,
    keywords,
    length,
    generateTweetMutation,
  ]);

  const regenerateTweet = useCallback(async () => {
    if (!validateInputs()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!tone) {
      toast.error("Please select a tone");
      return;
    }

    await regenerateTweetMutation.mutateAsync({
      topic: topic.trim(),
      tone,
      audience: audience.trim(),
      keywords: keywords.trim() || null,
      length,
    });
  }, [
    validateInputs,
    tone,
    topic,
    audience,
    keywords,
    length,
    regenerateTweetMutation,
  ]);

  const copyToClipboard = useCallback(async () => {
    if (!generatedTweet) {
      toast.error("No tweet to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedTweet);
      toast.success("Tweet copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  }, [generatedTweet]);

  const saveToHistory = useCallback(async () => {
    if (!generatedTweet) {
      toast.error("No tweet to save");
      return;
    }

    // Tweet is already saved during generation
    toast.success("Tweet saved to history!");
  }, [generatedTweet]);

  const exportCurrentToCsv = useCallback(
    async (filename: string) => {
      if (!generatedTweet) {
        toast.error("No tweet to export");
        return;
      }

      try {
        // Create CSV content
        const headers = [
          "Topic",
          "Tone",
          "Audience",
          "Keywords",
          "Length",
          "Content",
          "Character Count",
          "Generated At",
        ];
        const row = [
          topic,
          tone || "",
          audience,
          keywords || "",
          length,
          generatedTweet,
          charCount.toString(),
          lastGeneratedAt?.toISOString() || "",
        ];

        // Escape CSV values
        const escapeCsvValue = (value: string) => {
          if (value.includes(",") || value.includes('"') || value.includes("\n")) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        };

        const csvContent = [
          headers.join(","),
          row.map(escapeCsvValue).join(","),
        ].join("\n");

        // Create and download file
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success("Tweet exported successfully!");
      } catch (error) {
        toast.error("Failed to export tweet");
      }
    },
    [generatedTweet, topic, tone, audience, keywords, length, charCount, lastGeneratedAt],
  );

  return {
    // State
    topic,
    tone,
    audience,
    keywords,
    length,
    generatedTweet,
    charCount,
    lastGeneratedAt,
    isGenerating: generateTweetMutation.isPending || regenerateTweetMutation.isPending,
    isSaving: false, // Saving happens automatically during generation
    isCopying: false,
    isExporting: false,
    isExportDialogOpen,
    exportFilename,
    validation: {
      topicError,
      toneError,
      audienceError,
    },

    // Mutations
    setTopic,
    setTone,
    setAudience,
    setKeywords,
    setLength,
    openExportDialog,
    closeExportDialog,
    setExportFilename,
    resetCompose,
    clearValidation,

    // Commands
    generateTweet,
    regenerateTweet,
    copyToClipboard,
    saveToHistory,
    exportCurrentToCsv,
  };
}